import { useId } from 'react';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import type { LeaveOfAbsenceListItem } from '@/features/section-attribute/types';
import { useFormatDate } from '@/hooks/date';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
type Props = CFormItemProps & {
  value?: LeaveOfAbsenceListItem[];
};
function LabelValue({
  label,
  value
}: {
  label: string;
  value?: string;
}) {
  return <VStack className="gap-0.5">
      <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
        {label}
      </Text>
      <Text variant="p">{value || '-'}</Text>
    </VStack>;
}
function LeaveOfAbsenceReadCard({
  item,
  formatDate
}: {
  item: LeaveOfAbsenceListItem;
  formatDate: ReturnType<typeof useFormatDate>;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.leaveOfAbsenceItem'
  });
  const startDate = item.leaveStartDate ? formatDate(new Date(item.leaveStartDate), {
    preset: 'date'
  }) : undefined;
  const endDate = item.leaveEndDate ? formatDate(new Date(item.leaveEndDate), {
    preset: 'date'
  }) : undefined;
  return <VStack className="gap-3 border-l-2 border-neutral-muted-border pl-4 py-2 w-full">
      <LabelValue label={t('kind')} value={item.kindLabel ?? undefined} />
      <HStack className="gap-8">
        <LabelValue label={t('startDate')} value={startDate} />
        <LabelValue label={t('endDate')} value={endDate} />
      </HStack>
    </VStack>;
}

/**
 * 休職リストの表示を行う CFormItem（Readonly専用）
 *
 * 各項目: kind, kindLabel, leaveStartDate, leaveEndDate
 */
export const CFormLeaveOfAbsenceListItem = buildCFormItem<Props>((props: Props) => {
  const formatDate = useFormatDate();
  const idPrefix = useId();
  const values = props.value ?? [];
  const readComponent = values.length > 0 ? <VStack className="gap-4 w-full">
          {values.map((item, index) => <LeaveOfAbsenceReadCard key={`${idPrefix}-read-${index}`} item={item} formatDate={formatDate} />)}
        </VStack> : <Text variant="pSmall" className="text-neutral-muted-on-fill">
          -
        </Text>;
  return <CFormItemBase {...props} readonly hideActionButtons readComponent={readComponent} />;
});